import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  LayoutDashboard, Film, CalendarDays, Headphones, Eye, LogOut, Plus,
  Trash2, Pencil, Search, Upload, Play, X, Lock,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { GameBoard } from "../components/GameBoard";
import { DatePicker } from "../components/DatePicker";
import { istTodayStr } from "../lib/ist";

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "movies", label: "Movies", icon: Film },
  { id: "schedule", label: "Schedule", icon: CalendarDays },
  { id: "dialogue", label: "Dialogue", icon: Headphones },
  { id: "preview", label: "Preview", icon: Eye },
];

const empty = {
  title: "", year: "", director: "", genre: "", language: "", description: "",
  tagline: "", plot_hint: "", cast: "", alt_titles: [], clues: [], active: true,
};

export const Admin = () => {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("dashboard");

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem("ac_admin_token");
      if (!token) { setChecking(false); return; }
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        localStorage.removeItem("ac_admin_token");
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("ac_admin_token");
    setUser(null);
  };

  if (checking) return <div className="p-10 text-center text-zinc-400">Loading…</div>;
  if (!user) return <LoginForm onLogin={setUser} />;

  return (
    <div className="min-h-screen">
      <header className="ac-glass sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Film className="w-5 h-5 ac-gold-text" />
            <span className="font-display font-black tracking-widest ac-gold-text">ADMIN PANEL</span>
          </div>
          <button onClick={logout} data-testid="admin-logout"
            className="flex items-center gap-2 text-sm text-zinc-400 hover:text-rose-300">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex flex-wrap gap-2 pb-4">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap transition-all ${
                tab === t.id ? "bg-amber-500/15 text-amber-200 border border-amber-500/30"
                : "text-zinc-400 hover:bg-white/5"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
        {tab === "dashboard" && <Dashboard />}
        {tab === "movies" && <Movies />}
        {tab === "schedule" && <Schedule />}
        {tab === "dialogue" && <DialogueManager />}
        {tab === "preview" && <Preview />}
      </div>
    </div>
  );
};

// ---------------- Login ----------------
const LoginForm = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.token) localStorage.setItem("ac_admin_token", data.token);
      onLogin(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <form onSubmit={submit} data-testid="admin-login-form"
        className="ac-card p-8 w-full max-w-sm marquee-border">
        <div className="text-center mb-6">
          <Lock className="w-8 h-8 mx-auto ac-gold-text mb-2" />
          <h1 className="font-display text-2xl font-black tracking-wider ac-gold-text">ADMIN LOGIN</h1>
          <p className="text-xs text-zinc-500 mt-1">ABSOLUTE CINEMA control panel</p>
        </div>
        <input data-testid="admin-email-input" type="email" placeholder="Email" value={email}
          onChange={(e) => setEmail(e.target.value)} required
          className="w-full mb-3 px-4 py-3 rounded-xl bg-black/50 border border-amber-500/25 text-zinc-100 focus:outline-none focus:border-amber-400/70" />
        <input data-testid="admin-password-input" type="password" placeholder="Password" value={password}
          onChange={(e) => setPassword(e.target.value)} required
          className="w-full mb-4 px-4 py-3 rounded-xl bg-black/50 border border-amber-500/25 text-zinc-100 focus:outline-none focus:border-amber-400/70" />
        {error && <div data-testid="admin-login-error" className="text-rose-400 text-sm mb-3">{error}</div>}
        <button data-testid="admin-login-button" disabled={loading}
          className="btn-gold w-full py-3 rounded-xl font-black tracking-wider">
          {loading ? "SIGNING IN…" : "SIGN IN"}
        </button>
      </form>
    </div>
  );
};

// ---------------- Dashboard ----------------
const Dashboard = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setStats(r.data)).catch(() => {}); }, []);
  if (!stats) return <div className="text-zinc-400">Loading…</div>;
  const cards = [
    ["Total Movies", stats.total_movies], ["Published", stats.published_movies],
    ["Upcoming", stats.upcoming_movies], ["Scheduled", stats.scheduled_challenges],
    ["Total Games", stats.total_games], ["Daily Plays", stats.daily_plays],
    ["Dialogue Plays", stats.dialogue_challenges], ["Wins", stats.wins], ["Losses", stats.losses],
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="admin-dashboard">
      {cards.map(([label, val]) => (
        <div key={label} className="ac-card p-5">
          <div className="text-3xl font-black text-amber-100">{val}</div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 mt-1">{label}</div>
        </div>
      ))}
    </div>
  );
};

// ---------------- Movies ----------------
const Movies = () => {
  const [movies, setMovies] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);

  const load = async (query = "") => {
    const { data } = await api.get("/admin/movies", { params: { q: query } });
    setMovies(data);
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this movie?")) return;
    await api.delete(`/admin/movies/${id}`);
    toast.success("Movie deleted");
    load(q);
  };

  return (
    <div data-testid="admin-movies">
      <div className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input data-testid="admin-movie-search" value={q}
            onChange={(e) => { setQ(e.target.value); load(e.target.value); }}
            placeholder="Search movies…"
            className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-black/50 border border-amber-500/25 text-zinc-100 focus:outline-none focus:border-amber-400/70" />
        </div>
        <button data-testid="admin-add-movie" onClick={() => setEditing(empty)}
          className="btn-gold px-4 rounded-xl font-bold flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>
      <div className="text-xs text-zinc-500 mb-2">{movies.length} movies</div>
      <div className="space-y-2 max-h-[60vh] overflow-auto">
        {movies.map((m) => (
          <div key={m.id} data-testid={`admin-movie-${m.id}`}
            className="ac-card p-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="font-semibold text-zinc-100 truncate">
                {m.title} <span className="text-amber-400/70 font-mono-ac text-sm">{m.year}</span>
                {!m.active && <span className="ml-2 text-[10px] text-rose-400">DISABLED</span>}
              </div>
              <div className="text-xs text-zinc-500 truncate">{m.director} · {m.genre} · {m.language}</div>
            </div>
            <div className="flex gap-1 shrink-0">
              <button onClick={() => setEditing(m)} data-testid={`admin-edit-${m.id}`}
                className="p-2 rounded-lg hover:bg-white/5 text-amber-300"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => del(m.id)} data-testid={`admin-delete-${m.id}`}
                className="p-2 rounded-lg hover:bg-white/5 text-rose-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
      {editing && <MovieForm movie={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(q); }} />}
    </div>
  );
};

const MovieForm = ({ movie, onClose, onSaved }) => {
  const [form, setForm] = useState({
    ...empty, ...movie,
    year: movie.year || "",
    alt_titles: (movie.alt_titles || []).join(", "),
    clues: (movie.clues || []).concat(["", "", "", "", ""]).slice(0, 5),
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    const payload = {
      ...form,
      year: form.year ? parseInt(form.year, 10) : null,
      alt_titles: form.alt_titles ? form.alt_titles.split(",").map((s) => s.trim()).filter(Boolean) : [],
      clues: form.clues.filter((c) => c && c.trim()).length === 5 ? form.clues : [],
    };
    try {
      if (movie.id) await api.put(`/admin/movies/${movie.id}`, payload);
      else await api.post("/admin/movies", payload);
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const field = (k, label, type = "text") => (
    <div>
      <label className="text-xs uppercase tracking-wider text-zinc-500">{label}</label>
      <input type={type} data-testid={`movie-field-${k}`} value={form[k] ?? ""}
        onChange={(e) => set(k, e.target.value)}
        className="w-full mt-1 px-3 py-2 rounded-lg bg-black/50 border border-amber-500/25 text-zinc-100 focus:outline-none focus:border-amber-400/70" />
    </div>
  );

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="ac-card p-6 w-full max-w-lg max-h-[88vh] overflow-auto" onClick={(e) => e.stopPropagation()}
        data-testid="movie-form">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-bold text-amber-100">{movie.id ? "Edit Movie" : "Add Movie"}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-zinc-400" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">{field("title", "Title")}</div>
          {field("year", "Year", "number")}
          {field("language", "Language")}
          {field("director", "Director")}
          {field("genre", "Genre")}
          <div className="col-span-2">{field("alt_titles", "Alternative Titles (comma separated)")}</div>
          <div className="col-span-2">{field("tagline", "Tagline")}</div>
          <div className="col-span-2">
            <label className="text-xs uppercase tracking-wider text-zinc-500">Plot Hint / Description</label>
            <textarea data-testid="movie-field-plot_hint" value={form.plot_hint}
              onChange={(e) => set("plot_hint", e.target.value)} rows={2}
              className="w-full mt-1 px-3 py-2 rounded-lg bg-black/50 border border-amber-500/25 text-zinc-100 focus:outline-none focus:border-amber-400/70" />
          </div>
          {field("cast", "Cast")}
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm text-zinc-300">
              <input type="checkbox" data-testid="movie-field-active" checked={form.active}
                onChange={(e) => set("active", e.target.checked)} /> Active
            </label>
          </div>
          <div className="col-span-2">
            <label className="text-xs uppercase tracking-wider text-zinc-500">Custom Clues (all 5 to override auto)</label>
            {form.clues.map((c, i) => (
              <input key={i} data-testid={`movie-clue-${i + 1}`} value={c}
                onChange={(e) => set("clues", form.clues.map((x, j) => (j === i ? e.target.value : x)))}
                placeholder={`Clue ${i + 1} (optional)`}
                className="w-full mt-2 px-3 py-2 rounded-lg bg-black/50 border border-amber-500/25 text-zinc-100 text-sm focus:outline-none focus:border-amber-400/70" />
            ))}
          </div>
        </div>
        <button data-testid="movie-save-button" onClick={save} disabled={saving || !form.title}
          className="btn-gold w-full py-3 rounded-xl font-black tracking-wider mt-5">
          {saving ? "SAVING…" : "SAVE MOVIE"}
        </button>
      </div>
    </div>
  );
};

// ---------------- Schedule ----------------
const Schedule = () => {
  const [mode, setMode] = useState("daily");
  const [list, setList] = useState([]);
  const [date, setDate] = useState("");
  const [movieId, setMovieId] = useState("");
  const [movieQ, setMovieQ] = useState("");
  const [movieResults, setMovieResults] = useState([]);

  const load = async (m = mode) => {
    const { data } = await api.get("/admin/schedule", { params: { mode: m } });
    setList(data);
  };
  useEffect(() => { load(mode); }, [mode]);

  useEffect(() => {
    if (!movieQ.trim()) { setMovieResults([]); return; }
    const t = setTimeout(async () => {
      const { data } = await api.get("/admin/movies", { params: { q: movieQ } });
      setMovieResults(data.slice(0, 8));
    }, 200);
    return () => clearTimeout(t);
  }, [movieQ]);

  const add = async () => {
    if (!date || !movieId) { toast.error("Pick a date and a movie"); return; }
    await api.post("/admin/schedule", { mode, date, movie_id: movieId });
    toast.success("Scheduled");
    setDate(""); setMovieId(""); setMovieQ(""); setMovieResults([]);
    load(mode);
  };
  const del = async (d) => {
    await api.delete("/admin/schedule", { params: { mode, date: d } });
    load(mode);
  };

  return (
    <div data-testid="admin-schedule">
      <div className="flex gap-2 mb-4">
        {["daily", "dialogue"].map((m) => (
          <button key={m} onClick={() => setMode(m)} data-testid={`schedule-mode-${m}`}
            className={`px-4 py-2 rounded-lg text-sm font-bold capitalize ${
              mode === m ? "bg-amber-500/15 text-amber-200 border border-amber-500/30" : "text-zinc-400 hover:bg-white/5"}`}>
            {m}
          </button>
        ))}
      </div>

      <div className="ac-card p-4 mb-5 space-y-3">
        <div className="text-sm font-bold text-amber-200">Assign Date → Movie</div>
        <DatePicker value={date} onChange={setDate} testid="schedule-date-input" placeholder="Pick a date to schedule" />
        <div className="relative">
          <input data-testid="schedule-movie-search" value={movieQ}
            onChange={(e) => { setMovieQ(e.target.value); setMovieId(""); }}
            placeholder="Search movie to assign…"
            className="w-full px-3 py-2 rounded-lg bg-black/50 border border-amber-500/25 text-zinc-100 focus:outline-none focus:border-amber-400/70" />
          {movieResults.length > 0 && !movieId && (
            <div className="absolute z-30 mt-1 w-full ac-glass rounded-lg max-h-52 overflow-auto">
              {movieResults.map((m) => (
                <button key={m.id} data-testid={`schedule-pick-${m.id}`}
                  onClick={() => { setMovieId(m.id); setMovieQ(`${m.title} — ${m.year}`); setMovieResults([]); }}
                  className="w-full text-left px-3 py-2 hover:bg-amber-500/10 text-sm text-zinc-200">
                  {m.title} <span className="text-amber-400/70">{m.year}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <button data-testid="schedule-add-button" onClick={add} className="btn-gold w-full py-2.5 rounded-lg font-bold">
          Schedule
        </button>
      </div>

      <div className="space-y-2 max-h-[50vh] overflow-auto">
        {list.map((s) => (
          <div key={s.date} data-testid={`schedule-row-${s.date}`}
            className="ac-card p-3 flex items-center justify-between">
            <div>
              <div className="font-mono-ac text-amber-200 text-sm">{s.date}</div>
              <div className="text-zinc-300 text-sm">{s.movie_title} <span className="text-zinc-500">{s.movie_year}</span></div>
            </div>
            <button onClick={() => del(s.date)} data-testid={`schedule-delete-${s.date}`}
              className="p-2 rounded-lg hover:bg-white/5 text-rose-400"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------------- Dialogue Manager ----------------
const DialogueManager = () => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [movie, setMovie] = useState(null);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      const { data } = await api.get("/admin/movies", { params: { q } });
      setResults(data.slice(0, 8));
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const loadMovie = async (id) => {
    const { data } = await api.get(`/admin/movies/${id}`);
    setMovie(data);
    setResults([]);
    setQ(data.title);
  };

  const [reencoding, setReencoding] = useState(false);
  const reencode = async () => {
    if (!movie) return;
    setReencoding(true);
    try {
      const { data } = await api.post(`/admin/movies/${movie.id}/reencode-dialogues`);
      toast.success(`Converted to MP3: slots ${data.reencoded.join(", ") || "none"}` +
        (data.failed?.length ? ` · failed: ${data.failed.join(", ")}` : ""));
      loadMovie(movie.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Re-encode failed");
    } finally {
      setReencoding(false);
    }
  };

  return (
    <div data-testid="admin-dialogue">
      <div className="ac-card p-4 mb-4">
        <div className="font-display text-lg text-amber-100 mb-1">🎧 Dialogue Clip Management</div>
        <p className="text-sm text-zinc-400">
          Search a movie below, then upload / preview / replace its 5 audio clips
          (MP3 or WAV). These clips are used for BOTH the Daily and Dialogue challenges.
          Slots without an upload play a placeholder tone.
        </p>
      </div>
      <div className="relative mb-5">
        <input data-testid="dialogue-movie-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search a movie to manage its dialogue clips…"
          className="w-full px-4 py-3 rounded-xl bg-black/50 border border-amber-500/25 text-zinc-100 focus:outline-none focus:border-amber-400/70" />
        {results.length > 0 && (
          <div className="absolute z-30 mt-1 w-full ac-glass rounded-lg max-h-52 overflow-auto">
            {results.map((m) => (
              <button key={m.id} onClick={() => loadMovie(m.id)} data-testid={`dialogue-pick-${m.id}`}
                className="w-full text-left px-3 py-2 hover:bg-amber-500/10 text-sm text-zinc-200">
                {m.title} <span className="text-amber-400/70">{m.year}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {!movie && (
        <div className="ac-card p-8 text-center text-zinc-500 text-sm" data-testid="dialogue-empty">
          Search and select a movie above to see its 5 upload slots.
        </div>
      )}

      {movie && (
        <div className="ac-card p-5">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="font-display text-xl text-amber-100">{movie.title} — {movie.year}</div>
            {(movie.dialogues || []).length > 0 && (
              <button
                data-testid="dialogue-reencode-button"
                onClick={reencode}
                disabled={reencoding}
                className="px-4 py-2 rounded-lg border border-amber-500/30 text-amber-200 text-sm font-bold hover:bg-amber-500/10 disabled:opacity-50"
                title="Convert all uploaded clips to MP3 for universal playback"
              >
                {reencoding ? "Converting…" : "↻ Fix playback (convert to MP3)"}
              </button>
            )}
          </div>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((slot) => {
              const has = (movie.dialogues || []).some((d) => d.slot === slot);
              return <DialogueSlot key={slot} movieId={movie.id} slot={slot} has={has}
                onChange={() => loadMovie(movie.id)} />;
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const DialogueSlot = ({ movieId, slot, has, onChange }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/movies/${movieId}/dialogue/${slot}`, fd);
      toast.success(`Uploaded clip ${slot}`);
      onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setUploading(false); }
  };

  const preview = async () => {
    try {
      const resp = await api.get(`/admin/movies/${movieId}/dialogue/${slot}/preview`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const audio = new Audio(url);
      audio.addEventListener("ended", () => URL.revokeObjectURL(url), { once: true });
      audio.addEventListener("error", () => URL.revokeObjectURL(url), { once: true });
      await audio.play().catch(() => {
        URL.revokeObjectURL(url);
        toast.error("Preview failed — this file may be an unsupported audio format");
      });
    } catch {
      toast.error("Preview failed — could not load the clip");
    }
  };

  const del = async () => {
    await api.delete(`/admin/movies/${movieId}/dialogue/${slot}`);
    toast.success(`Removed clip ${slot}`);
    onChange();
  };

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-black/30 border border-amber-500/15" data-testid={`dialogue-slot-${slot}`}>
      <div className="w-8 h-8 rounded-full bg-amber-500/15 flex items-center justify-center text-amber-300 font-bold text-sm">{slot}</div>
      <div className="flex-1 text-sm text-zinc-300">
        Dialogue {slot} {has ? <span className="text-emerald-400 text-xs">· uploaded</span> : <span className="text-zinc-500 text-xs">· placeholder tone</span>}
      </div>
      <button onClick={preview} data-testid={`dialogue-preview-${slot}`} className="p-2 rounded-lg hover:bg-white/5 text-amber-300"><Play className="w-4 h-4" /></button>
      <button onClick={() => fileRef.current?.click()} data-testid={`dialogue-upload-${slot}`}
        className="p-2 rounded-lg hover:bg-white/5 text-amber-300" title="Upload">
        {uploading ? <span className="text-xs">…</span> : <Upload className="w-4 h-4" />}
      </button>
      {has && <button onClick={del} data-testid={`dialogue-remove-${slot}`} className="p-2 rounded-lg hover:bg-white/5 text-rose-400"><Trash2 className="w-4 h-4" /></button>}
      <input ref={fileRef} type="file" accept="audio/*" className="hidden" onChange={upload} />
    </div>
  );
};

// ---------------- Preview ----------------
const Preview = () => {
  const [mode, setMode] = useState("daily");
  const [date, setDate] = useState(istTodayStr());
  const [go, setGo] = useState(0);
  return (
    <div data-testid="admin-preview">
      <div className="ac-card p-4 mb-5 flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-xs uppercase tracking-wider text-zinc-500">Mode</label>
          <div className="flex gap-2 mt-1">
            {["daily", "dialogue"].map((m) => (
              <button key={m} onClick={() => setMode(m)} data-testid={`preview-mode-${m}`}
                className={`px-3 py-2 rounded-lg text-sm font-bold capitalize ${mode === m ? "bg-amber-500/15 text-amber-200 border border-amber-500/30" : "text-zinc-400 hover:bg-white/5"}`}>{m}</button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-zinc-500">Date</label>
          <div className="mt-1 w-56">
            <DatePicker value={date} onChange={setDate} testid="preview-date-input" />
          </div>
        </div>
        <button data-testid="preview-load-button" onClick={() => setGo((g) => g + 1)} className="btn-gold px-5 py-2 rounded-lg font-bold">
          Preview Game
        </button>
      </div>
      <p className="text-xs text-zinc-500 mb-3">Preview mode does not affect player statistics.</p>
      {go > 0 && (
        <div className="max-w-2xl">
          <GameBoard key={`${mode}-${date}-${go}`} backendMode={mode} storageMode={mode}
            date={date} preview showCountdown={false} modeLabel="PREVIEW" />
        </div>
      )}
    </div>
  );
};
